import React, { useState, useCallback, useMemo } from 'react'
import Select from 'react-select'
import { debounce } from 'lodash'
import { useSearchParams } from 'react-router-dom'
import { postListImages } from '../../services/images.service'
import {
  selectStyles,
  borderlessSelectStyles,
  yearsOptions,
  genresList,
  themesList,
  demographicsOptions,
  statusOptions,
  sortOptions,
  DEFAULT_FILTER,
} from '../../data/searchBar.js'

export function SearchBar({ filterBy, onSetFilter }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [isOpen, setIsOpen] = useState(false)
  const [localFilter, setLocalFilter] = useState({ ...DEFAULT_FILTER, ...filterBy })

  const updateUrl = useCallback((f) => {
    const p = new URLSearchParams()
    Object.entries(f).forEach(([k, v]) => {
      if (Array.isArray(v)) v.forEach(x => x && p.append(k, x))
      else if (typeof v === 'boolean') {
        if (k === 'top100' && v) p.set(k, 'true')
        if (k === 'excludeMatureContent' && !v) p.set(k, 'false')
      } else if (v) {
        p.set(k, v)
      }
    })
    setSearchParams(p, { replace: true })
  }, [setSearchParams])

  const pushFilter = useCallback((f) => {
    onSetFilter({ ...f, page: 1 })
    updateUrl(f)
  }, [onSetFilter, updateUrl])

  const debouncedPush = useMemo(() => debounce(pushFilter, 800), [pushFilter])

  const handleFieldChange = (key, value) => {
    const next = { ...localFilter, [key]: value }
    setLocalFilter(next)

    if (key === 'title') {
      const clean = value.trim()
      if (!clean) {
        debouncedPush.cancel()
        pushFilter(next)
      } else if (clean.length >= 3) {
        debouncedPush(next)
      }
    } else {
      debouncedPush.cancel()
      pushFilter(next)
    }
  }

  const handleInputChange = e => {
    const { name, type, value, checked } = e.target
    handleFieldChange(name, type === 'checkbox' ? checked : value)
  }

  const handleMulti = (sel, key) =>
    handleFieldChange(key, sel ? sel.map(o => o.value) : [])

  const handleSingle = (sel, key) =>
    handleFieldChange(key, sel ? sel.value : '')

  const clearFilters = () => {
    setLocalFilter(DEFAULT_FILTER)
    debouncedPush.cancel()
    pushFilter(DEFAULT_FILTER)
  }

  const isFilterActive = Object.keys(DEFAULT_FILTER).some(
    k => JSON.stringify(localFilter[k]) !== JSON.stringify(DEFAULT_FILTER[k])
  )

  return (
    <div className="w-full flex flex-col gap-4" itemScope itemType="https://schema.org/SearchAction">
      <meta itemProp="target" content="/animes?title={title}" />

      <div className="lg:flex items-center gap-8">
        <h1 className="mb-4 lg:mb-0 text-white text-center text-5xl">
          כל האנימות
        </h1>
        <div className="lg:flex items-center gap-16">
          <div className="lg:w-[350px] mb-4 lg:mb-0">
            <div className="flex items-center gap-3 p-2 border border-[rgba(131,130,120,0.37)]">
              <img src={postListImages.searchIcon} alt="" className="w-6 h-6" />
              <input
                aria-label="חיפוש שם אנימה"
                type="search"
                name="title"
                value={localFilter.title}
                onChange={handleInputChange}
                placeholder="הכנס שם אנימה..."
                className="flex-1 ml-2 bg-transparent text-softText focus:outline-none"
                itemProp="query-input"
              />
              {localFilter.title && (
                <span
                  className="text-muted text-sm cursor-pointer"
                  onClick={() => handleFieldChange('title', '')}
                >
                  ניקוי
                </span>
              )}
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-4">
            <SelectBlock
              title="קטגוריה"
              options={genresList}
              value={genresList.filter(g => localFilter.genres.includes(g.value))}
              isMulti
              onChange={o => handleMulti(o, 'genres')}
            />
            <SelectBlock
              title="תת קטגוריה"
              options={themesList}
              value={themesList.filter(t => localFilter.themes.includes(t.value))}
              isMulti
              onChange={o => handleMulti(o, 'themes')}
            />
            <SelectBlock
              title="דמוגרפיה"
              options={demographicsOptions}
              value={demographicsOptions.filter(d => localFilter.demographics.includes(d.value))}
              isMulti
              onChange={o => handleMulti(o, 'demographics')}
            />
            <SelectBlock
              title="שנת יציאה"
              options={yearsOptions}
              value={yearsOptions.find(y => y.value === localFilter.year) || null}
              onChange={o => handleSingle(o, 'year')}
              width="lg:w-[120px]"
            />
          </div>
        </div>
      </div>

      <BottomBar
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        localFilter={localFilter}
        handleSingle={handleSingle}
        handleInputChange={handleInputChange}
        clearFilters={clearFilters}
        isFilterActive={isFilterActive}
      />
    </div>
  )
}

function SelectBlock({ title, options, value, onChange, isMulti = false, width = 'lg:w-[200px]' }) {
  return (
    <div className="flex flex-col lg:mb-8 gap-3">
      <h6 className="text-sm">{title}</h6>
      <div className={width}>
        <Select
          options={options}
          value={value}
          onChange={onChange}
          isMulti={isMulti}
          placeholder="הכל"
          styles={selectStyles}
        />
      </div>
    </div>
  )
}

function BottomBar({ isOpen, setIsOpen, localFilter, handleSingle, handleInputChange, clearFilters, isFilterActive }) {
  return (
    <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center">
      <button
        className="lg:hidden text-primary self-start mb-2"
        onClick={e => { e.preventDefault(); setIsOpen(!isOpen) }}
      >
        סינון ומיון ▾
      </button>

      <div className={`${isOpen ? 'block' : 'hidden'} lg:flex gap-4`}>
        <div className="flex gap-5 lg:w-[320px]">
          {sortOptions.map(op => (
            <button
              key={op.value}
              className={localFilter.sort === op.value ? 'text-primary' : 'text-muted'}
              onClick={e => { e.preventDefault(); handleSingle({ value: op.value }, 'sort') }}
            >
              {op.label}
            </button>
          ))}
        </div>
        <div className="bg-gray-700 w-[1px] hidden lg:block" />
        <div className="lg:w-48">
          <Select
            isClearable
            options={statusOptions}
            value={statusOptions.find(o => o.value === localFilter.status) || null}
            onChange={o => handleSingle(o, 'status')}
            placeholder="סטטוס שידור"
            styles={borderlessSelectStyles}
          />
        </div>
        <div className="bg-gray-700 w-[1px] hidden lg:block" />
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            name="excludeMatureContent"
            checked={localFilter.excludeMatureContent}
            onChange={handleInputChange}
            className="accent-primary w-[18px] h-[18px]"
          />
          <span className="text-muted">סינון תוכן 18+</span>
        </label>
      </div>

      <div className="flex justify-center items-center gap-3">
        <button
          type="button"
          onClick={clearFilters}
          className={isFilterActive ? 'text-primary' : 'text-muted'}
        >
          נקה פילטרים
        </button>
        <svg xmlns="http://www.w3.org/2000/svg" width="6" height="6">
          <circle cx="3" cy="3" r="2.5" fill="#838278" />
        </svg>
        <h6 className="text-muted text-sm">26,387 אנימות באתר</h6>
      </div>
    </div>
  )
}
